// src/components/Header/Header.tsx - Updated Version
import React, { Suspense, lazy } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BsChatLeftTextFill, BsGearFill } from 'react-icons/bs';

import LogoBox from '@/components/LogoBox';
import StyledHeader from './StyledHeader';
import { HeaderProvider } from '@/context/HeaderContext';
import { HeaderProps } from '@/types/header';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import LanguageSwitcher from '@/components/layout/Header/LanguageSwitcher';
import clsx from 'clsx';
import { useStores } from '@/stores';
import { toJS } from 'mobx';

// Lazy load components for better performance
const AppMenu = lazy(() => import('./AppMenu'));
const MobileHeader = lazy(() => import('./MobileHeader'));
const EnhancedSearchBar = lazy(() => import('./EnhancedSearchBar'));
const NotificationDropdown = lazy(() => import('./NotificationDropdown'));
const ProfileDropdown = lazy(() => import('./ProfileDropdown'));

const HeaderContent: React.FC<HeaderProps> = ({
    showSearch = true,
    showNotifications = true,
    showProfile = true,
    customActions,
    variant = 'default'
}) => {
    const { t } = useTranslation();
    const { authStore } = useStores();
    const { isAuthenticated } = authStore;

    return (
        <>
            {/* Mobile Header */}
            <div className="lg:hidden fixed-top w-full">
                <Suspense fallback={<div className="h-16 bg-white" />}>
                    <MobileHeader />
                </Suspense>
            </div>

            {/* Desktop Header */}
            <div className="hidden lg:block !m-0 shadow-sm">
                <StyledHeader variant={variant}>
                    <div className="container-fluid px-3">
                        <div className="w-full flex align-items-center justify-content-between whitespace-nowrap space-x-6">
                            {/* Left Section - Logo + Search */}
                            <div className="flex align-items-center flex-shrink-0">
                                <LogoBox />
                                {showSearch && (
                                    <div className="d-none d-md-block ms-3">
                                        <Suspense fallback={<LoadingSpinner size="sm" />}>
                                            <EnhancedSearchBar
                                                placeholder={t('search.placeholder')}
                                                className="search-container"
                                                showFilters={false}
                                            />
                                        </Suspense>
                                    </div>
                                )}
                            </div>

                            {/* Center Section - Navigation Menu */}
                            <div className="flex-grow-1 hidden lg:block">
                                <Suspense fallback={<LoadingSpinner size="sm" />}>
                                    <AppMenu />
                                </Suspense>
                            </div>

                            {/* Right Section - Actions & User */}
                            <div className="flex flex-shrink-0">
                                <ul className="nav flex-nowrap align-items-center list-unstyled m-0">
                                    {/* Language Switcher */}
                                    <li className="nav-item">
                                        <LanguageSwitcher
                                            variant="icon"
                                            size="sm"
                                            className="nav-item ms-2"
                                        />
                                    </li>
                                    {/* Custom Actions */}
                                    {customActions}

                                    {/* Messaging */}
                                    <li className="nav-item ms-2">
                                        <Link                                            
                                            className={clsx(
                                                "nav-link bg-light icon-md btn btn-light p-0",
                                                "pointer-events-none !text-gray-300" // TODO REWORK
                                            )}
                                            to="/messaging"
                                            title={t('header.messaging')}
                                        >
                                            <BsChatLeftTextFill size={15} />
                                        </Link>
                                    </li>

                                    {/* Settings */}
                                    <li className="nav-item ms-2">
                                        <Link
                                            className={clsx(
                                                "nav-link bg-light icon-md btn btn-light p-0",
                                                "pointer-events-none !text-gray-300" // TODO REWORK
                                            )}
                                            to="/settings/account"
                                            title={t('header.settings')}
                                        >
                                            <BsGearFill size={15} />
                                        </Link>
                                    </li>

                                    {/* Notifications */}
                                    {(isAuthenticated && showNotifications) && (
                                        <li className="nav-item ms-2">
                                            <Suspense fallback={<LoadingSpinner size="sm" />}>
                                                <NotificationDropdown />
                                            </Suspense>
                                        </li>
                                    )}

                                    {/* Profile */}
                                    {showProfile && (
                                        <li className="nav-item ms-2">
                                            <Suspense fallback={<LoadingSpinner size="sm" />}>
                                                <ProfileDropdown />
                                            </Suspense>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        </div>
                    </div>
                </StyledHeader>
            </div>
        </>
    );
};

const Header: React.FC<HeaderProps> = (props) => {
    return (
        <HeaderProvider>
            <HeaderContent {...props} />
        </HeaderProvider>
    );
};

export default Header;

// Export individual components for specific use cases
export { AppMenu, MobileHeader, EnhancedSearchBar, NotificationDropdown, ProfileDropdown };
